/**
 * FAQs — tira de categorías scrollable que conmuta paneles.
 *
 * Las categorías son secciones `faq-category` hermanas de la sección `faq`, y
 * las pestañas se construyen aquí a partir de ellas: así el orden y los
 * títulos salen de la propia plantilla y no hay una lista duplicada que
 * mantener en sync.
 *
 * El acordeón de cada panel lo resuelve el custom element `collapsible-section`
 * (assets/base.js) vía `data-accordion-set`, así que de él solo nos ocupamos
 * del anclado al abrir (ver más abajo); lo demás es la selección de categoría
 * y el scroll horizontal de la tira.
 */
if (!customElements.get("faq-tabs")) {
  customElements.define(
    "faq-tabs",
    class FaqTabs extends HTMLElement {
      /** Debe cuadrar con la duración de `brn-faq-item-out` en faq.css */
      static LEAVE_MS = 180;

      connectedCallback() {
        this.tabsWrapper = this.querySelector(".brn-faq__tabs");
        this.list = this.querySelector(".brn-faq__tabs-list");
        this.arrow = this.querySelector(".brn-faq__tabs-arrow");

        if (!this.list) return;

        this.handleClick = this.handleClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.updateOverflow = this.updateOverflow.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.build = this.build.bind(this);

        this.addEventListener("click", this.handleClick);
        this.list.addEventListener("keydown", this.handleKeydown);
        this.list.addEventListener("scroll", this.updateOverflow, { passive: true });
        window.addEventListener("resize", this.handleResize);

        // En el editor de temas se añaden, quitan y reordenan categorías en vivo
        document.addEventListener("shopify:section:load", this.build);
        document.addEventListener("shopify:section:unload", this.build);
        document.addEventListener("shopify:section:reorder", this.build);

        this.build();
      }

      disconnectedCallback() {
        clearTimeout(this.swapTimer);
        this.removeEventListener("click", this.handleClick);
        this.list?.removeEventListener("keydown", this.handleKeydown);
        this.list?.removeEventListener("scroll", this.updateOverflow);
        window.removeEventListener("resize", this.handleResize);
        document.removeEventListener("shopify:section:load", this.build);
        document.removeEventListener("shopify:section:unload", this.build);
        document.removeEventListener("shopify:section:reorder", this.build);
      }

      /** (Re)construye las pestañas a partir de las secciones de categoría. */
      build() {
        this.panels = Array.from(document.querySelectorAll("[data-faq-category]"));

        if (!this.panels.length) {
          this.list.replaceChildren();
          this.tabs = [];
          this.hidden = true;
          return;
        }

        this.hidden = false;

        const previous = this.tabs?.findIndex(
          (tab) => tab.getAttribute("aria-selected") === "true",
        );

        this.tabs = this.panels.map((panel, index) => {
          const tab = document.createElement("button");
          tab.type = "button";
          tab.className = "brn-faq__tab";
          tab.setAttribute("role", "tab");
          tab.textContent = panel.dataset.title || "";

          const id = panel.dataset.sectionId || String(index);
          tab.id = `FaqTab-${id}`;
          panel.setAttribute("aria-labelledby", tab.id);
          panel.setAttribute("role", "tabpanel");
          if (!panel.id) panel.id = `FaqPanel-${id}`;
          tab.setAttribute("aria-controls", panel.id);

          return tab;
        });

        this.list.replaceChildren(...this.tabs);

        const initial = previous > -1 && previous < this.tabs.length ? previous : 0;
        this.select(initial, { animate: false });
        this.updateOverflow();
      }

      handleResize() {
        this.updateOverflow();
      }


      handleClick(event) {
        const arrow = event.target.closest(".brn-faq__tabs-arrow");
        if (arrow && this.contains(arrow)) {
          this.nudgeArrow();
          this.scrollTabs();
          return;
        }

        const tab = event.target.closest(".brn-faq__tab");
        if (!tab || !this.contains(tab)) return;

        this.select(this.tabs.indexOf(tab));
      }

      handleKeydown(event) {
        const current = this.tabs.indexOf(document.activeElement);
        if (current === -1) return;

        const keys = {
          ArrowRight: current + 1,
          ArrowLeft: current - 1,
          Home: 0,
          End: this.tabs.length - 1,
        };

        if (!(event.key in keys)) return;

        event.preventDefault();
        const next = Math.max(0, Math.min(this.tabs.length - 1, keys[event.key]));
        this.select(next);
        this.tabs[next].focus();
      }

      select(index, options) {
        const animate = !options || options.animate !== false;
        const target = this.panels[index];
        if (!target) return;

        this.tabs.forEach((tab, i) => {
          const isActive = i === index;
          tab.setAttribute("aria-selected", isActive ? "true" : "false");
          tab.setAttribute("tabindex", isActive ? "0" : "-1");
          tab.classList.toggle("brn-faq__tab--active", isActive);
        });

        this.scrollTabIntoView(this.tabs[index], animate);

        const current = this.panels.find((panel) => !panel.hasAttribute("hidden"));
        const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        clearTimeout(this.swapTimer);

        // Sin animación de salida no hay nada que esperar: se conmuta y listo
        if (!animate || reduceMotion || !current || current === target) {
          this.showPanel(target, animate && !reduceMotion);
          return;
        }

        // La categoría que sale se desvanece antes de que entre la nueva; las
        // dos son secciones apiladas, así que no pueden solaparse en layout.
        current.classList.remove("brn-faq__panel--enter");
        current.classList.add("brn-faq__panel--leave");

        this.swapTimer = setTimeout(() => {
          this.showPanel(target, true);
        }, FaqTabs.LEAVE_MS);
      }

      showPanel(target, animate) {
        this.panels.forEach((panel) => {
          const isTarget = panel === target;

          panel.classList.remove("brn-faq__panel--leave", "brn-faq__panel--enter");
          panel.toggleAttribute("hidden", !isTarget);

          if (isTarget && animate) {
            // Reinicia la cascada de entrada en cada cambio de categoría
            void panel.offsetWidth;
            panel.classList.add("brn-faq__panel--enter");
          }
        });
      }

      scrollTabIntoView(tab, animate) {
        if (!tab) return;

        // Contra el padding de la lista, no contra su borde: en móvil la tira
        // sangra hasta el borde de la tarjeta y ese padding es lo que mantiene
        // las pestañas alineadas con el título.
        const listStyle = getComputedStyle(this.list);
        const padLeft = parseFloat(listStyle.paddingLeft) || 0;
        const padRight = parseFloat(listStyle.paddingRight) || 0;

        const listRect = this.list.getBoundingClientRect();
        const tabRect = tab.getBoundingClientRect();
        const left = listRect.left + padLeft;
        const right = listRect.right - padRight;
        let delta = 0;

        if (tabRect.left < left) {
          delta = tabRect.left - left;
        } else if (tabRect.right > right) {
          delta = tabRect.right - right;
        }

        if (delta === 0) return;

        this.list.scrollBy({ left: delta, behavior: animate ? "smooth" : "auto" });
      }

      /** Empujoncito de la flecha al pulsarla, hacia donde va a scrollear. */
      nudgeArrow() {
        if (!this.arrow) return;

        // Reinicia la animación si se pulsa de nuevo antes de que termine
        this.arrow.classList.remove("brn-faq__tabs-arrow--nudge");
        void this.arrow.offsetWidth;
        this.arrow.classList.add("brn-faq__tabs-arrow--nudge");
      }

      scrollTabs() {
        const amount = this.list.clientWidth * 0.8;
        const direction = this.tabsWrapper.dataset.direction === "prev" ? -1 : 1;

        this.list.scrollBy({ left: amount * direction, behavior: "smooth" });
      }

      updateOverflow() {
        if (!this.list) return;

        const { scrollLeft, scrollWidth, clientWidth } = this.list;
        const hasOverflow = scrollWidth - clientWidth > 1;
        const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;

        this.tabsWrapper.classList.toggle("brn-faq__tabs--static", !hasOverflow);
        this.tabsWrapper.dataset.direction = atEnd ? "prev" : "next";

        if (this.arrow) {
          this.arrow.setAttribute(
            "aria-label",
            atEnd ? this.dataset.labelPrev || "" : this.dataset.labelNext || "",
          );
        }
      }
    },
  );

  /* --- Anclado al abrir una pregunta ---------------------------------------
     Toda pregunta que se abre se recoloca en su sitio, quepa o no la
     respuesta en el área visible. El scroll acompaña a la expansión fotograma
     a fotograma, persiguiendo el sitio donde la pregunta debe acabar: un único
     movimiento continuo, no un salto al final.
     ------------------------------------------------------------------------- */

  /** Aire contra los bordes del área visible al recolocar. */
  const ANCHOR_MARGIN = 24;

  /** Cubre la transición de `.brn-faq__answer` en faq.css, con holgura. */
  const ANCHOR_MS = 500;

  /** Fracción de la distancia pendiente que se recorre en cada fotograma. */
  const ANCHOR_FOLLOW = 0.22;

  /** Corta el seguimiento en curso, si lo hay. */
  let anchorRunning = null;

  /** El primer ancestro que scrollea de verdad; `null` si scrollea la ventana. */
  function scrollParent(el) {
    for (let node = el.parentElement; node; node = node.parentElement) {
      const overflowY = getComputedStyle(node).overflowY;
      const scrolls = overflowY === "auto" || overflowY === "scroll";
      if (scrolls && node.scrollHeight > node.clientHeight) return node;
    }

    return null;
  }

  /**
   * Área realmente visible. Cuando scrollea la ventana hay que descontar el
   * header, que es fijo y en esta plantilla va superpuesto al contenido: sin
   * eso la pregunta se ancla debajo de él y queda tapada.
   */
  function viewportOf(scroller) {
    if (scroller) {
      const rect = scroller.getBoundingClientRect();
      return { top: rect.top, height: rect.height };
    }

    const header = document.querySelector(".page-header");
    const top =
      header && getComputedStyle(header).position === "fixed"
        ? header.getBoundingClientRect().height
        : 0;

    return { top, height: window.innerHeight - top };
  }

  /**
   * Dónde debe quedar el borde superior del item: siempre arriba, quepa o no
   * la respuesta, para que la pregunta abierta caiga en el mismo sitio.
   */
  function anchorTop(view) {
    return view.top + ANCHOR_MARGIN;
  }

  /**
   * Acompaña la expansión hasta dejar `item` en su sitio.
   *
   * No se puede lanzar un scroll con la distancia final calculada de antemano:
   * la respuesta está colapsada en una fila `0fr`, así que todavía no mide nada
   * y el documento aún no ha crecido —el navegador recortaría el
   * desplazamiento a lo que hubiera disponible en ese instante—. En lugar de
   * predecir, se persigue la geometría real en cada fotograma, que además
   * absorbe de paso lo que encoge la pregunta que el acordeón cierre a la vez.
   */
  function anchorItem(item) {
    // Abrir otra pregunta mientras la anterior se coloca cancela aquel
    // seguimiento: dos persiguiendo destinos distintos se pelean por el scroll.
    anchorRunning?.();

    const scroller = scrollParent(item);
    const target = scroller || window;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      // faq.css desactiva ahí la transición: en cuanto el acordeón conmuta, la
      // respuesta ya está desplegada y basta con colocarse de una vez.
      requestAnimationFrame(() => {
        const top = item.getBoundingClientRect().top - anchorTop(viewportOf(scroller));
        target.scrollBy({ top, behavior: "auto" });
      });
      return;
    }

    const events = ["wheel", "touchstart", "keydown"];
    let done = false;

    const stop = () => {
      done = true;
      if (anchorRunning === stop) anchorRunning = null;
      events.forEach((type) => window.removeEventListener(type, stop));
    };

    // Si el usuario toma el control del scroll se le deja: nada de pelear.
    events.forEach((type) => window.addEventListener(type, stop, { passive: true }));
    anchorRunning = stop;

    const start = performance.now();

    const step = () => {
      if (done) return;

      const delta = item.getBoundingClientRect().top - anchorTop(viewportOf(scroller));

      if (performance.now() - start < ANCHOR_MS) {
        if (Math.abs(delta) >= 1) {
          target.scrollBy({ top: delta * ANCHOR_FOLLOW, behavior: "auto" });
        }

        requestAnimationFrame(step);
        return;
      }

      // El documento ya tiene su alto final, así que lo que quede se salda
      // exacto. Con el seguimiento hecho es cuestión de décimas de píxel.
      if (Math.abs(delta) >= 1) target.scrollBy({ top: delta, behavior: "auto" });
      stop();
    };

    requestAnimationFrame(step);
  }

  // En captura, antes de que `collapsible-section` conmute el acordeón desde su
  // listener en burbuja: así se sabe si lo que viene es una apertura.
  document.addEventListener(
    "click",
    (event) => {
      const trigger = event.target.closest?.(".brn-faq__question");
      const item = trigger?.closest(".brn-faq__item");
      if (!item || !item.closest("[data-faq-category]")) return;

      // Al cerrar no hay contenido nuevo que enseñar, así que no se recoloca.
      if (item.dataset.open === "true") return;

      anchorItem(item);
    },
    true,
  );
}
